import { useEffect, useRef, useState, useCallback } from "react";

const IDLE_MS = 10 * 60 * 1000; // 10 minutes
const WARN_MS = 60 * 1000;      // warn 60s before

/**
 * Auto-logout after IDLE_MS of no user activity.
 * Shows a warning modal 60s before actual logout so the user can extend.
 */
export function useIdleLogout(onLogout, enabled = true) {
  const [warnOpen, setWarnOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const lastActivityRef = useRef(Date.now());
  const tickRef = useRef(null);

  const bump = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (warnOpen) setWarnOpen(false);
  }, [warnOpen]);

  const stayConnected = useCallback(() => {
    lastActivityRef.current = Date.now();
    setWarnOpen(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= IDLE_MS) {
        setWarnOpen(false);
        onLogout();
      } else if (elapsed >= IDLE_MS - WARN_MS) {
        const left = Math.max(0, Math.round((IDLE_MS - elapsed) / 1000));
        setSecondsLeft(left);
        setWarnOpen(true);
      }
    }, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [enabled, bump, onLogout]);

  return { warnOpen, secondsLeft, stayConnected };
}
